import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const cellCls = "w-16 border border-gray-300 rounded-sm px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-[#BF9B30]";
const TKEYS = ["t1_days", "t2_days", "t3_days", "t4_days", "t5_days", "ttf_days"];
const TLABELS = ["T1 Approval", "T2 Sourcing", "T3 Selection", "T4 Offer", "T5 Joining", "Total TTF"];

export default function MrfConfig() {
  const { can } = useAuth();
  const editable = can("mrf_config", "write");
  const [targets, setTargets] = useState([]);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/mrf-config").then(({ data }) => { setTargets(data.targets); setRules(data.rules); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const saveTarget = async (t) => {
    setBusy(t.grade);
    try {
      await api.put(`/mrf-config/targets/${encodeURIComponent(t.grade)}`, t);
      toast.success(`SLA targets updated for ${t.grade}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const saveRule = async (r) => {
    setBusy(r.rule_id);
    try {
      await api.put(`/mrf-config/rules/${r.rule_id}`, { sla_hours_per_level: r.sla_hours_per_level });
      toast.success(`SLA hours updated — ${r.name}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div data-testid="mrf-config-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">SLA Targets & Approval Rules</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-8">Stage targets are in days; approval-level SLAs are in hours. Breaches auto-escalate to the next level.</p>

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Accountability clock targets (days)</h2>
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto mb-10">
        <table className="w-full text-sm" data-testid="sla-targets-table">
          <thead>
            <tr className="border-b border-gray-200 bg-[#F5F6F8]">
              <th className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Grade</th>
              {TLABELS.map((l) => <th key={l} className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{l}</th>)}
              {editable && <th className="px-3 py-3" />}
            </tr>
          </thead>
          <tbody>
            {targets.map((t, ti) => (
              <tr key={t.grade} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2.5 font-medium text-[#0E2240] whitespace-nowrap">{t.grade}</td>
                {TKEYS.map((k) => (
                  <td key={k} className="px-3 py-2 text-right">
                    {editable ? (
                      <input data-testid={`target-${ti}-${k}`} type="number" min="0.5" step="0.5" className={cellCls} value={t[k]}
                        onChange={(e) => setTargets((p) => p.map((x, i) => i === ti ? { ...x, [k]: e.target.value } : x))} />
                    ) : <span className="tabular-nums">{t[k]}</span>}
                  </td>
                ))}
                {editable && (
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`target-save-${ti}`} disabled={busy === t.grade} onClick={() => saveTarget(t)}
                      className="text-xs text-[#1E5AA8] hover:underline disabled:opacity-50">Save</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Approval matrix (evaluated top-down, first match wins)</h2>
      <div className="space-y-4" data-testid="approval-rules-list">
        {rules.map((r, ri) => (
          <div key={r.rule_id} className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium text-[#0E2240]">{r.name}</div>
                <div className="text-xs text-[#6B7280] mt-0.5">
                  Scope: {r.scope} · Types: {r.requisition_types.length ? r.requisition_types.join(", ") : "Any"} · Grades: {r.grades.length ? r.grades.join(", ") : "Any"}
                </div>
              </div>
              {editable && (
                <button data-testid={`rule-save-${ri}`} disabled={busy === r.rule_id} onClick={() => saveRule(r)}
                  className="text-xs text-[#1E5AA8] hover:underline disabled:opacity-50">Save SLA hours</button>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {r.approval_levels.map((role, li) => (
                <div key={li} className="flex items-center gap-2">
                  <div className="border border-gray-200 rounded-sm px-3 py-1.5 bg-[#F5F6F8]">
                    <div className="text-xs font-medium text-[#0E2240]">L{li + 1} · {role.replace(/_/g, " ")}</div>
                    <div className="flex items-center gap-1 mt-1">
                      {editable ? (
                        <input data-testid={`rule-${ri}-sla-${li}`} type="number" min="1" className="w-14 border border-gray-300 rounded-sm px-1.5 py-0.5 text-xs text-right tabular-nums"
                          value={r.sla_hours_per_level[li] ?? ""}
                          onChange={(e) => setRules((p) => p.map((x, i) => {
                            if (i !== ri) return x;
                            const hrs = [...x.sla_hours_per_level];
                            hrs[li] = e.target.value;
                            return { ...x, sla_hours_per_level: hrs };
                          }))} />
                      ) : <span className="text-xs tabular-nums">{r.sla_hours_per_level[li]}</span>}
                      <span className="text-[10px] text-[#6B7280]">hrs</span>
                    </div>
                  </div>
                  {li < r.approval_levels.length - 1 && <span className="text-[#6B7280]">→</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
